/**
 * ChartTimeframeStrip.tsx — MER Algo Footprints v9.2.0 — Phase 3 commit 3.2
 *
 * Pill strip + horizon selector for the chart panel header. Replaces the
 * previously read-only "5min" label with discoverable, clickable controls.
 *
 * Three sub-components composed inline:
 *   - Chart bar interval pill strip (per-symbol)
 *   - Horizon dropdown (global, "all symbols")
 *   - Transient toast (only when saveHorizon API fails)
 *
 * P3-D3 / D-P3J: the 1h, 4h and 1D pills are ordinary chart controls. They were
 * non-fetching "coming soon" pills under D-GCLIENT2-2(c) while the server lanes
 * were unbuilt; position 15 banked all of them (1h native, 4h p15-4h-rth-v1, 1D
 * p15-1d-eod-v1), so the advisory gate and its banner were retired. The selected
 * canonical token is passed through unchanged — never coerced to a served
 * intraday interval. 1W remains out of scope for the chart strip (D-P3J-4) and
 * is offered only by the canonical ANALYSIS TF selector below.
 *
 * Reads / writes:
 *   useStore().symbolData[symbol].interval  (per-symbol chart bar interval)
 *   useStore().horizon                       (global trader horizon)
 *   useStore().setInterval(sym, interval)
 *   useStore().setHorizon(horizon)
 *   fetchSymbol(sym, interval)               (re-fetch on interval change)
 *   saveHorizon(horizon)                     (persist horizon to Postgres)
 *
 * Side effects:
 *   logEvent('ai', '...')  for every interval / horizon change (telemetry).
 *
 * Failure modes:
 *   saveHorizon failure → optimistic store update is preserved; transient
 *   toast displayed for ~3.5s, error logged to event log.
 */

import React, { useState, useCallback } from 'react';
import { useStore } from '../../store';
import { useMarketData } from '../../shared/hooks/useMarketData';
import { toFetchableInterval } from '../../shared/hooks/useMarketData';
import { saveHorizon } from '../../data/userPrefsApi';
import { logEvent } from '../../features/status/systemEventLog';
import type { TraderHorizon } from '../../types';
// P1-Q4 G-CLIENT-1 (position 8) — canonical analysis selection and advisory
// capability display. Deliberately distinct from the chart bar interval below:
// the canonical timeframe is global, never symbol-scoped, and never triggers a
// market-data fetch.
import {
  CANONICAL_CLIENT_TIMEFRAMES,
  CanonicalClientTimeframe,
  CanonicalClientHorizon,
  selectPairAdvisory,
  SelectedPairAdvisory,
} from '../../shared/capabilities/gClient1Capability';
import '../../styles/chartTimeframeStrip.css';

// ── Pill definitions ─────────────────────────────────────────────────────────

type IntervalLabel = 'tick' | '1sec' | '5sec' | '15sec' | '30sec'
                   | '1min' | '5min' | '15min'
                   | '1h' | '4h' | '1D';

interface PillDef {
  value:   IntervalLabel;
  label:   string;
  group:   'sub-minute' | 'standard' | 'extended';
}

const PILLS: PillDef[] = [
  { value: 'tick',  label: 'tick',  group: 'sub-minute' },
  { value: '1sec',  label: '1s',    group: 'sub-minute' },
  { value: '5sec',  label: '5s',    group: 'sub-minute' },
  { value: '15sec', label: '15s',   group: 'sub-minute' },
  { value: '30sec', label: '30s',   group: 'sub-minute' },
  { value: '1min',  label: '1m',    group: 'standard' },
  { value: '5min',  label: '5m',    group: 'standard' },
  { value: '15min', label: '15m',   group: 'standard' },
  { value: '1h',    label: '1h',    group: 'extended' },
  { value: '4h',    label: '4h',    group: 'extended' },
  { value: '1D',    label: '1D',    group: 'extended' },
];

// ── Horizon options ──────────────────────────────────────────────────────────

interface HorizonOption {
  value:        TraderHorizon;
  label:        string;
  available:    boolean;
}

const HORIZON_OPTIONS: HorizonOption[] = [
  { value: 'scalp',    label: 'Scalp',     available: true },
  { value: 'intraday', label: 'Intraday',  available: true },
  { value: 'swing',    label: 'Swing',     available: false },
  { value: 'position', label: 'Position',  available: false },
];

// ── Password-manager opt-out (Phase 3 commit 3.2.3) ──────────────────────────
//
// Browser password managers (Dashlane, 1Password, LastPass, Bitwarden) inject
// content scripts that scan the DOM for form-like elements and intercept
// click/focus events for autofill. Even non-form <button>s get hijacked when
// the manager's heuristic flags them. Symptom: clicks never reach React's
// onClick handler.
//
// Each manager has a different opt-out signal; spreading all of them on the
// element is the documented approach. autocomplete="off" handles native
// browser autofill as a bonus.

const IGNORE_PW_MGR = {
  'autoComplete':      'off',         // browser native
  'data-form-type':    'other',       // Dashlane
  'data-1p-ignore':    'true',        // 1Password
  'data-lpignore':     'true',        // LastPass
  'data-bwignore':     'true',        // Bitwarden
} as const;

// ── P1-Q4 G-CLIENT-1 advisory selector ───────────────────────────────────────

/**
 * Canonical analysis-timeframe selector plus advisory capability display.
 *
 * Presentational and pure with respect to its props so the banked semantics can
 * be asserted behaviorally in the focused Position-8 test. It renders all seven
 * canonical timeframes including `1W`, states currency explicitly in text
 * (CURRENT versus LAST_KNOWN, never styling alone), shows exact banked reason
 * codes, and keeps transport/version/validation diagnostics in a separate line
 * from capability reasons.
 *
 * Selecting a canonical value calls `onSelect` only. It never sets a chart
 * interval, never fetches market data, and never posts a shadow observation.
 */
export function CanonicalAnalysisSelector(props: {
  value:    CanonicalClientTimeframe;
  advisory: SelectedPairAdvisory;
  onSelect: (tf: CanonicalClientTimeframe) => void;
}) {
  const { value, advisory, onSelect } = props;

  const currencyText =
    advisory.status === 'CURRENT'      ? 'Capability: current'
    : advisory.status === 'LAST_KNOWN' ? 'Capability: last known (not current)'
    :                                    'Capability: unavailable';

  return (
    <div className="cts-canonical-wrap" data-testid="canonical-analysis">
      <span className="cts-canonical-label">ANALYSIS TF</span>
      <select
        className="cts-canonical-select"
        data-testid="canonical-timeframe-select"
        value={value}
        onChange={e => onSelect(e.target.value as CanonicalClientTimeframe)}
        title="Analysis timeframe — global canonical selection, not the chart data interval"
        {...IGNORE_PW_MGR}
      >
        {CANONICAL_CLIENT_TIMEFRAMES.map(tf => (
          <option key={tf} value={tf}>{tf}</option>
        ))}
      </select>

      <span className="cts-canonical-currency" data-testid="capability-currency">
        {currencyText}
      </span>

      {advisory.cell && (
        <span className="cts-canonical-state" data-testid="capability-state">
          {advisory.horizon}/{advisory.timeframe}: {advisory.cell.staticState}
          {advisory.cell.reasonCodes.length > 0
            ? ` — ${advisory.cell.reasonCodes.join(', ')}`
            : ''}
        </span>
      )}

      {advisory.diagnostic && (
        <span className="cts-canonical-diagnostic" data-testid="capability-diagnostic">
          Capability fetch diagnostic: {advisory.diagnostic.kind}
        </span>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

export function ChartTimeframeStrip({ symbol }: { symbol: string }) {
  // ── Store reads ──
  const data       = useStore(s => s.symbolData[symbol]);
  const horizon    = useStore(s => s.horizon);
  const setIntvl   = useStore(s => s.setInterval);
  const setHrzn    = useStore(s => s.setHorizon);
  const { fetchSymbol } = useMarketData();

  // ── P1-Q4 G-CLIENT-1 store reads (global; not symbol-scoped) ──
  const canonicalTimeframe  = useStore(s => s.canonicalTimeframe);
  const setCanonicalTf      = useStore(s => s.setCanonicalTimeframe);
  const currentOverlay      = useStore(s => s.currentCapabilityOverlay);
  const lastKnownOverlay    = useStore(s => s.lastKnownCapabilityOverlay);
  const capabilityDiagnostic = useStore(s => s.capabilityDiagnostic);

  const currentInterval = data?.interval ?? '5min';

  // ── Toast state (only when saveHorizon fails) ──
  const [toast, setToast] = useState<string | null>(null);

  // ── Pill click handler ──
  const onPillClick = useCallback((pill: PillDef) => {
    if (pill.value === currentInterval) return;   // no-op

    // G-CLIENT-2: a non-fetchable interval is an explicit non-fetching outcome —
    // never a silent 5min substitute. Every current pill value is fetchable, so
    // this narrows fail-closed rather than gating: the canonical token passes
    // through unchanged (D-P3J-5).
    const fetchable = toFetchableInterval(pill.value);
    if (fetchable === null) return;

    setIntvl(symbol, pill.value);
    fetchSymbol(symbol, fetchable);
    logEvent('ai', `Chart interval → ${pill.value}`, symbol);
  }, [currentInterval, setIntvl, fetchSymbol, symbol]);

  // ── Horizon change handler ──
  const onHorizonChange = useCallback(async (newHorizon: TraderHorizon) => {
    if (newHorizon === horizon) return;

    // Optimistic update — instant UI reflection, doesn't block.
    setHrzn(newHorizon);
    logEvent('ai', `Trader horizon → ${newHorizon}`, null);

    // Background API call. Failure leaves the optimistic update in place
    // and surfaces a transient toast.
    try {
      await saveHorizon(newHorizon);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown';
      logEvent('ai', `Couldn't save horizon (${reason}) — using local value`, null);
      setToast(`Couldn't save preference (${reason})`);
      setTimeout(() => setToast(null), 3500);
    }
  }, [horizon, setHrzn]);

  // ── Render ──
  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }}>
      <div className="cts-root">

        {/* Pill group: sub-minute */}
        <div className="cts-pill-group">
          {PILLS.filter(p => p.group === 'sub-minute').map(pill => (
            <button
              key={pill.value}
              type="button"
              className={`cts-pill${pill.value === currentInterval ? ' cts-pill--active' : ''}`}
              onClick={() => onPillClick(pill)}
              title={`Set chart interval to ${pill.label}`}
              {...IGNORE_PW_MGR}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div className="cts-sep" />

        {/* Pill group: standard */}
        <div className="cts-pill-group">
          {PILLS.filter(p => p.group === 'standard').map(pill => (
            <button
              key={pill.value}
              type="button"
              className={`cts-pill${pill.value === currentInterval ? ' cts-pill--active' : ''}`}
              onClick={() => onPillClick(pill)}
              title={`Set chart interval to ${pill.label}`}
              {...IGNORE_PW_MGR}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div className="cts-sep" />

        {/* Pill group: extended (1h native, 4h and 1D server-derived) */}
        <div className="cts-pill-group">
          {PILLS.filter(p => p.group === 'extended').map(pill => (
            <button
              key={pill.value}
              type="button"
              className={`cts-pill${pill.value === currentInterval ? ' cts-pill--active' : ''}`}
              onClick={() => onPillClick(pill)}
              title={`Set chart interval to ${pill.label}`}
              {...IGNORE_PW_MGR}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* Horizon selector */}
        <div className="cts-horizon-wrap">
          <span className="cts-horizon-label">HRZN</span>
          <select
            className="cts-horizon-select"
            value={horizon}
            onChange={e => onHorizonChange(e.target.value as TraderHorizon)}
            title="Trader horizon — global, applies to all symbols"
            {...IGNORE_PW_MGR}
          >
            {HORIZON_OPTIONS.map(opt => (
              <option
                key={opt.value}
                value={opt.value}
              >
                {opt.label}{opt.available ? '' : '*'}
              </option>
            ))}
          </select>
          <span className="cts-horizon-suffix">(all symbols)</span>
        </div>

        {/* P1-Q4 G-CLIENT-1: canonical analysis timeframe + advisory capability.
            Separate from the chart interval pills above; changing it performs no
            market-data fetch and no shadow observation. */}
        <CanonicalAnalysisSelector
          value={canonicalTimeframe}
          advisory={selectPairAdvisory({
            horizon:    horizon as CanonicalClientHorizon,
            timeframe:  canonicalTimeframe,
            current:    currentOverlay,
            lastKnown:  lastKnownOverlay,
            diagnostic: capabilityDiagnostic,
          })}
          onSelect={setCanonicalTf}
        />

        {/* Save-failure toast */}
        {toast && <div className="cts-toast">{toast}</div>}
      </div>
    </div>
  );
}
